import React from 'react';
import { ReactComponent as ChevronIcon } from 'assets/icons/ic_ArrowFilledGrey.svg';
import clsx from 'clsx';

import type IOrgTreeViewProps from './interfaces/IOrgTreeViewProps';

import styles from './scss/OrgPathField.module.scss';

function OrgTreeView({
  items,
  value,
  openIndexes,
  toggleOpen,
  onClickItem,
  level = 0,
  isFirstLevel = true,
  currentOrgPath = '',
}: IOrgTreeViewProps) {
  const isChildOfSelected = (itemPath: string): boolean => {
    if (!currentOrgPath) return false;
    const cleanCurrentPath = currentOrgPath.split('/').map((part) => part.trim()).join('/');
    const cleanItemPath = itemPath.split('/').map((part) => part.trim()).join('/');
    return cleanItemPath.startsWith(`${cleanCurrentPath}/`);
  };

  return (
    <ul style={{ listStyle: 'none', marginLeft: level === 1 ? 7 : level * 15, paddingLeft: 0 }}>
      {items.map((item, index) => {
        const isBranchOpen = openIndexes[item.orgPath];
        const isSelected = value === item.orgPath;
        const isFirstItem = isFirstLevel && index === 0;
        const isTopLevelItem = isFirstLevel && index === 0;
        const itemIsChildOfSelected = isChildOfSelected(item.orgPath);
        const isSelectionDisabled = Boolean(item.archived || itemIsChildOfSelected);
        const isExpandDisabled = Boolean(item.archived);
        let ariaLabel = item.name;
        if (item.archived) ariaLabel += ' (archived)';
        if (itemIsChildOfSelected) ariaLabel += ' (selection disabled - child of current path)';
        if (item.children.length > 0) ariaLabel += ' (expandable)';

        return (
          <li key={item.orgPath}>
            <div
              className={clsx(
                styles.treeItem,
                item.archived && styles.archived,
                isSelected && styles.selected,
                isBranchOpen && styles.expanded,
                isTopLevelItem && styles.topLevelItem,
              )}
              tabIndex={isSelectionDisabled ? -1 : 0}
              aria-label={ariaLabel}
              aria-expanded={item.children.length > 0 ? isBranchOpen : undefined}
              aria-disabled={isSelectionDisabled}
              aria-selected={isSelected}
              role="treeitem"
            >
              <div className={styles.arrowWrapper}>
                {!isFirstItem && <div className={styles.arrowLine} />}
                {item.children.length > 0 && !isExpandDisabled && (
                  <div
                    className={clsx(styles.arrow, isBranchOpen && styles.expanded)}
                    aria-hidden="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOpen(item.orgPath);
                    }}
                  >
                    <ChevronIcon />
                  </div>
                )}
                {item.children.length > 0 && isExpandDisabled ? (
                  <div className={styles.arrow} aria-hidden="true">
                    <ChevronIcon />
                  </div>
                ) : null}
              </div>
              <div
                className={clsx(
                  styles.itemContent,
                  itemIsChildOfSelected && styles.disabledItem,
                )}
                onClick={() => {
                  // Always allow toggling expansion
                  if (item.children.length > 0) {
                    toggleOpen(item.orgPath);
                  }

                  // But only allow selection if not disabled
                  if (!isSelectionDisabled) {
                    onClickItem(item.orgPath, isSelectionDisabled, isTopLevelItem);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    // Always allow toggling expansion
                    if (item.children.length > 0) {
                      toggleOpen(item.orgPath);
                    }

                    // But only allow selection if not disabled
                    if (!isSelectionDisabled) {
                      onClickItem(item.orgPath, isSelectionDisabled, isTopLevelItem);
                    }
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {item.name}
              </div>
            </div>
            {item.children.length > 0 && isBranchOpen && !item.archived ? (
              <OrgTreeView
                items={item.children}
                value={value}
                openIndexes={openIndexes}
                toggleOpen={toggleOpen}
                onClickItem={onClickItem}
                level={level + 1}
                isFirstLevel={false}
                currentOrgPath={currentOrgPath}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

OrgTreeView.defaultProps = {
  level: 0,
  isFirstLevel: true,
  currentOrgPath: '',
};

export default OrgTreeView;
